import {
  CONDITIONS,
  DEFAULT_CATEGORY_KEYS,
  DEFAULT_ROOM_KEYS,
  DESTINATIONS,
  DIMENSION_UNITS,
  PROGRESS_STATUSES,
  STORAGE_LOCATION_KEYS,
  type Condition,
  type CustomCategory,
  type CustomRoom,
  type Destination,
  type DimensionUnit,
  type ProgressStatus,
} from '@easymoving/types';
import type { TFunction } from 'i18next';

export interface Option {
  value: string;
  label: string;
}

const ROOM_SET = new Set<string>(DEFAULT_ROOM_KEYS);
const CATEGORY_SET = new Set<string>(DEFAULT_CATEGORY_KEYS);

/** Resolve a stored room value (default enum key OR custom string) to a label. */
export function roomLabel(value: string | undefined, t: TFunction): string {
  if (!value) return '';
  return ROOM_SET.has(value) ? t(`enum.room.${value}`) : value;
}
export function categoryLabel(value: string | undefined, t: TFunction): string {
  if (!value) return '';
  return CATEGORY_SET.has(value) ? t(`enum.category.${value}`) : value;
}
export const destinationLabel = (d: Destination, t: TFunction) => t(`enum.destination.${d}`);
export const statusLabel = (s: ProgressStatus, t: TFunction) => t(`enum.status.${s}`);
export const conditionLabel = (c: Condition, t: TFunction) => t(`enum.condition.${c}`);
export const unitLabel = (u: DimensionUnit, t: TFunction) => t(`enum.unit.${u}`);

/** Default rooms (translated) + the user's custom rooms appended. */
export function roomOptions(t: TFunction, custom: CustomRoom[] = []): Option[] {
  const defaults = DEFAULT_ROOM_KEYS.map((k) => ({ value: k, label: t(`enum.room.${k}`) }));
  const customOpts = custom.map((r) => ({ value: r.name, label: r.name }));
  return [...defaults, ...customOpts];
}
export function categoryOptions(t: TFunction, custom: CustomCategory[] = []): Option[] {
  const defaults = DEFAULT_CATEGORY_KEYS.map((k) => ({ value: k, label: t(`enum.category.${k}`) }));
  const customOpts = custom.map((c) => ({ value: c.name, label: c.name }));
  return [...defaults, ...customOpts];
}
export const destinationOptions = (t: TFunction): Option[] =>
  DESTINATIONS.map((d) => ({ value: d, label: t(`enum.destination.${d}`) }));
export const statusOptions = (t: TFunction): Option[] =>
  PROGRESS_STATUSES.map((s) => ({ value: s, label: t(`enum.status.${s}`) }));
export const conditionOptions = (t: TFunction): Option[] =>
  CONDITIONS.map((c) => ({ value: c, label: t(`enum.condition.${c}`) }));
export const unitOptions = (t: TFunction): Option[] =>
  DIMENSION_UNITS.map((u) => ({ value: u, label: t(`enum.unit.${u}`) }));
export const storageLocationOptions = (t: TFunction): Option[] =>
  STORAGE_LOCATION_KEYS.map((k) => ({ value: k, label: t(`enum.storage.${k}`) }));
